import type { IPerformanceService } from "../../../editor/services/performance/common/performance";
import {
    formatBottleneckLine, formatGpuTimingSection, formatRenderStatsReport, formatReportFooter,
    formatReportHeader, formatScoreSection, type RenderStatsSnapshot,
} from "../common/performanceReport";
import { scorePerformance, type PerformanceScore } from "../../../editor/services/performance/common/performanceScore";
import { fetchSourceSection, SLOW_FRAME_MARKER } from "../../../base/browser/sourceSection";
import type { StripEvent } from "../../../base/common/eventStrip";
import type { IGpuTimingService, GpuTimingResult } from "../../webgl/common/gpuTiming";

/**
 * Build the human-readable performance report from the live {@link IPerformanceService}
 * — the single source of truth shared by the MCP `measure_website_performance` tool and
 * the "Analyze performance with AI" command, so both hand the AI the same evidence.
 */
export async function buildRenderStatsReport(performance: IPerformanceService): Promise<string> {
    const resources = performance.resourceCounts;
    return formatRenderStatsReport({
        fps: performance.fps,
        usedMemory: performance.usedMemory,
        gpuMemory: performance.gpuMemory,
        gpuMemorySavings: performance.gpuMemorySavings,
        targetFps: performance.targetFps,
        ratings: performance.ratings,
        renderStats: performance.renderStats,
        sceneTriangles: performance.sceneTriangles,
        loadedResources: resources ? {
            geometries: resources.get("geometry")?.alive ?? 0,
            materials: resources.get("material")?.alive ?? 0,
            textures: resources.get("texture")?.alive ?? 0,
        } : undefined,
        deviceProfile: performance.deviceProfile,
        renderers: performance.renderers,
        programs: performance.programInfo,
        shaderErrors: readShaderErrors(),
        history: performance.history,
        events: await enrichEventsWithSource(performance.events),
    });
}

/** Attach a SECTION of the offending script (around the LoAF offset) to the heaviest
 *  few long frames, so the AI gets the culprit code inline instead of having to fetch
 *  it. Best-effort — a blocked/failed fetch is dropped. */
async function enrichEventsWithSource(events: readonly StripEvent[]): Promise<RenderStatsSnapshot["events"]> {
    const janks = events
        .filter((e) => e.kind === "jank" && e.url && typeof e.charPosition === "number")
        .sort((a, b) => b.magnitude - a.magnitude)
        .slice(0, 5);
    if (!janks.length) return events;
    const sections = new Map<number, string>();
    await Promise.all(janks.map(async (e) => {
        const sec = await fetchSourceSection(e.url!, e.charPosition!, 120, SLOW_FRAME_MARKER);
        if (!sec) return;
        // lead with the RESOLVED location (original file:line:col via sourcemap, else the
        // served file:line:col) so the AI cites the real spot, not the generated offset.
        const loc = sec.sourcePath ? `${sec.sourcePath}:${sec.sourceLine}:${sec.sourceColumn}` : e.url!;
        const head = `// ${loc}${sec.originalName ? ` (${sec.originalName})` : ""}\n`;
        sections.set(e.at, head + sec.text);
    }));
    return sections.size ? events.map((e) => (sections.has(e.at) ? { ...e, source: sections.get(e.at) } : e)) : events;
}

/**
 * The WHOLE report: provenance, score, the read, measured GPU time, the stats,
 * and where to go next.
 *
 * Lives here rather than in the panel because the panel is not the only reader —
 * the MCP tool and "Analyze performance with AI" hand the same document to an AI,
 * and while the panel assembled it privately those two silently got less than the
 * copy button did. The score is computed HERE from the service so a caller cannot
 * forget it; pass one in only to match a frozen (paused) view.
 */
export async function buildPerformanceReport(
    performance: IPerformanceService,
    options: {
        productName: string;
        url?: string;
        at: Date | number;
        /** false when a device preset is selected — see scorePerformance */
        targetIsThisDevice?: boolean;
        /** the preset's name, when one is selected */
        targetLabel?: string | null;
        /** a measurement to include; omitted sections are simply absent */
        gpuTiming?: Parameters<typeof formatGpuTimingSection>[0];
        /** score override — use the panel's when the view is paused */
        score?: PerformanceScore | null;
        includeFooter?: boolean;
    },
): Promise<string> {
    const targetFps = performance.targetFps ?? 60;
    const history = performance.history ?? [];
    const score = options.score ?? scorePerformance({
        profile: performance.deviceProfile ?? {},
        targetIsThisDevice: options.targetIsThisDevice !== false,
        targetFps: performance.targetFps,
        fps: performance.fps,
        fpsSamples: history.map(h => h.fps),
        gpuMemoryBytes: performance.gpuMemory,
        gpuMemoryWasteBytes: reclaimableVram(performance),
        trianglesPerFrame: performance.renderStats?.triangles,
        drawCalls: performance.renderStats?.drawCalls,
        transferredBytes: performance.totalResourceDownload,
    });

    const bottleneck = formatBottleneckLine({
        gpuMs: options.gpuTiming?.avgMs,
        targetFps,
        fps: performance.fps,
        drawCalls: performance.renderStats?.drawCalls,
    });

    const body = [
        formatScoreSection(score, options.targetLabel ?? null),
        bottleneck ? `${bottleneck}\n` : "",
        formatGpuTimingSection(options.gpuTiming, targetFps),
        await buildRenderStatsReport(performance),
    ].filter(Boolean).join("\n");

    return formatReportHeader({
        productName: options.productName,
        url: options.url,
        at: options.at,
        observedSeconds: history.length,
    })
        + "\n" + body
        + (options.includeFooter === false ? "" : formatReportFooter());
}

/**
 * Shader failures captured by the extension's client script.
 *
 * A global is the channel on purpose: the capture must happen BEFORE the page
 * builds its renderer (programs compile at startup), which is long before any
 * editor service exists — see armShaderErrors in needle-chrome.client.js. Absent
 * everywhere else, which is why this is a read with a guard and not a service.
 */
function readShaderErrors(): RenderStatsSnapshot["shaderErrors"] {
    const list = (globalThis as { __NEEDLE_SHADER_ERRORS__?: unknown }).__NEEDLE_SHADER_ERRORS__;
    if (!Array.isArray(list) || list.length === 0) return undefined;
    return list.filter(e => e && typeof e === "object") as RenderStatsSnapshot["shaderErrors"];
}

/** The four reclaimable-VRAM fixes are one number to the score. */
function reclaimableVram(performance: IPerformanceService): number | undefined {
    const s = performance.gpuMemorySavings;
    if (!s) return undefined;
    return s.compress + s.duplicate + (s.geometry ?? 0) + (s.hdr ?? 0);
}

/**
 * A GPU measurement on behalf of the AI, with the reason when there isn't one.
 *
 * Profiling wraps the page's draw calls for half a second, so it is the same paid
 * feature the panel gates. The note matters as much as the measurement: an AI
 * handed a report with no GPU section and no explanation concludes "the GPU is
 * fine" — the opposite of the truth we withheld.
 */
export async function gpuTimingForTool(
    service: IGpuTimingService | undefined,
    licensed: boolean,
    commerceEnabled = true,
): Promise<{ timing?: GpuTimingResult; note?: string }> {
    if (!service) return {}; // no renderer to time on this page — not a refusal
    if (!licensed) {
        return {
            note: (commerceEnabled
                ? "GPU timing was NOT measured: it requires Needle Inspector Pro (https://needle.tools/pricing). "
                : "GPU timing was NOT measured: it requires an existing Needle entitlement. ")
                + "Do not infer that the GPU is idle or fine from its absence — it was not measured at all.",
        };
    }
    const support = service.support();
    if (!support.supported) {
        return { note: `GPU timing was not measured: ${support.reason ?? `not available on ${support.backend}`}.` };
    }
    try {
        const timing = await service.measure();
        if (!timing) return { note: "GPU timing produced no samples — the page may not be rendering." };
        return { timing };
    }
    catch (err) {
        return { note: `GPU timing failed: ${err instanceof Error ? err.message : String(err)}.` };
    }
}
